// Generic, reusable focus-trap for modal layers (the parasha picker, plus the
// Journey/Peek drawers). Unlike the old `.pchip`-hard-wired trap, this computes
// the focusable set LIVE on every Tab (cards lazy-load) and supports layered /
// stacked modals via a module-level stack -- only the topmost trap handles Tab.

export interface TrapHandle {
  release(): void;
}

interface TrapOptions {
  initialFocus?: HTMLElement | null;
  onEscape?: () => void;
}

// CSS selector for natively-focusable / explicitly-focusable elements. We still
// exclude these *after* querying (jsdom-safe -- see focusable() below).
const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

// One live entry per active trap. The topmost entry (last) owns Tab handling.
interface TrapEntry {
  container: HTMLElement;
  handler: (e: KeyboardEvent) => void;
}

const stack: TrapEntry[] = [];

// Compute the focusable set LIVE. IMPORTANT jsdom caveat: do NOT filter by
// offsetParent / visibility -- jsdom reports offsetParent === null for every
// element, which would empty the set and break both the trap and the tests.
// Filter ONLY: not [hidden], not disabled, not aria-hidden="true". That stays
// jsdom-testable while remaining correct for our usage (focusables inside an
// open layer are never hidden).
function focusable(container: HTMLElement): HTMLElement[] {
  const nodes = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );
  return nodes.filter((el) => {
    if (el.hasAttribute("hidden")) return false;
    if ((el as HTMLButtonElement | HTMLInputElement).disabled) return false;
    if (el.getAttribute("aria-hidden") === "true") return false;
    return true;
  });
}

export function trapFocus(
  container: HTMLElement,
  opts: TrapOptions = {},
): TrapHandle {
  const entry: TrapEntry = {
    container,
    handler: (e: KeyboardEvent) => {
      // Only the topmost trap is active; previous layers are inert.
      if (stack[stack.length - 1] !== entry) return;

      if (e.key === "Escape") {
        opts.onEscape?.();
        return;
      }

      if (e.key !== "Tab") return;

      const items = focusable(container);
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inside = active != null && container.contains(active);

      if (e.shiftKey) {
        // Shift+Tab off the first element (or from outside) -> wrap to last.
        if (!inside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab off the last element (or from outside) -> wrap to first.
        if (!inside || active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
  };

  stack.push(entry);
  container.addEventListener("keydown", entry.handler);

  // On install: focus the requested element, else the first focusable.
  const target = opts.initialFocus ?? focusable(container)[0] ?? null;
  target?.focus();

  return {
    release() {
      const i = stack.indexOf(entry);
      if (i === -1) return; // already released
      // Pop THIS entry even if it is not the topmost; whatever remains last in
      // the stack automatically becomes the active trap again.
      stack.splice(i, 1);
      container.removeEventListener("keydown", entry.handler);
    },
  };
}
